import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { NumberField } from "components/inputs/NumberField"
import { ToggleCard } from "components/inputs/ToggleCard"

import { settingsService } from "services/settings/settings.service"
import { KYCSettings, WithdrawalSettings } from "services/settings/type"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface WithdrawalsTabProps {
  withdrawals: WithdrawalSettings
  kyc: KYCSettings
  onChange: (withdrawals: WithdrawalSettings) => void
  onKycChange: (kyc: KYCSettings) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const WithdrawalsTab: FC<WithdrawalsTabProps> = ({ withdrawals, kyc, onChange, onKycChange, refreshSettings, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const handleSave = async () => {
    setSaving(true)
    try {
      const [wSuccess, kSuccess] = await Promise.all([settingsService.updateWithdrawals(withdrawals), settingsService.updateKyc(kyc)])
      if (wSuccess && kSuccess) {
        toast.success(t("settings.withdrawals.updated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  const handleNumberChange = (field: keyof WithdrawalSettings, value: string) => {
    onChange({ ...withdrawals, [field]: parseFloat(value) || 0 })
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.withdrawals.kycTitle")}</h3>
        <SettingsHistoryNote target="withdrawals" refreshKey={historyKey} />
      </div>

      <ToggleCard
        label={t("settings.withdrawals.requireKyc")}
        checked={kyc.required}
        onChange={() => onKycChange({ ...kyc, required: !kyc.required })}
        disabled={saving}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <NumberField
          id="max-pending"
          label={t("settings.withdrawals.maxPending")}
          value={withdrawals.maxPendingPerUser ?? 0}
          onChange={(v) => handleNumberChange("maxPendingPerUser", v)}
          disabled={saving}
          hint={t("settings.withdrawals.maxPendingHint")}
          placeholder={t("settings.withdrawals.unlimitedPlaceholder")}
        />
        <NumberField
          id="kyc-threshold"
          label={t("settings.withdrawals.kycThreshold")}
          value={kyc.threshold ?? 0}
          onChange={(v) => onKycChange({ ...kyc, threshold: parseFloat(v) || 0 })}
          disabled={saving}
          hint={t("settings.withdrawals.kycThresholdHint")}
          centsDisplay
        />
      </div>

      <h3 className="text-xl font-semibold mt-4 mb-2 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.withdrawals.autoTitle")}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">{t("settings.withdrawals.autoDescription")}</p>

      <ToggleCard
        label={t("settings.withdrawals.enableAuto")}
        checked={withdrawals.autoApproveEnabled}
        onChange={() => onChange({ ...withdrawals, autoApproveEnabled: !withdrawals.autoApproveEnabled })}
        disabled={saving}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <NumberField
          id="auto-approve-max"
          label={t("settings.withdrawals.autoMaxAmount")}
          value={withdrawals.autoApproveMaxAmountCents ?? 0}
          onChange={(v) => handleNumberChange("autoApproveMaxAmountCents", v)}
          disabled={saving}
          hint={t("settings.withdrawals.autoMaxAmountHint")}
          placeholder={t("settings.withdrawals.autoMaxAmountPlaceholder")}
          centsDisplay
        />

        <div />

        <NumberField
          id="daily-limit-kyc"
          label={t("settings.withdrawals.dailyKyc")}
          value={withdrawals.dailyCountLimitKyc ?? 0}
          onChange={(v) => handleNumberChange("dailyCountLimitKyc", v)}
          disabled={saving}
          hint={t("settings.withdrawals.dailyHint")}
          placeholder={t("settings.withdrawals.unlimitedPlaceholder")}
        />
        <NumberField
          id="daily-limit-no-kyc"
          label={t("settings.withdrawals.dailyNoKyc")}
          value={withdrawals.dailyCountLimitNoKyc ?? 0}
          onChange={(v) => handleNumberChange("dailyCountLimitNoKyc", v)}
          disabled={saving}
          hint={t("settings.withdrawals.dailyHint")}
          placeholder={t("settings.withdrawals.unlimitedPlaceholder")}
        />
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTargets={[
          { id: "withdrawals", label: t("settings.withdrawals.autoTitle") },
          { id: "kyc", label: t("settings.withdrawals.kycTitle") }
        ]}
        onResetDone={handleResetDone}
      />
    </section>
  )
}
